import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, Ship, Ruler, Boxes, Minus, Plus, ArrowUpRight } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const inputCls = "h-11 w-full rounded-lg border border-black/10 bg-white px-3 text-[#1F1F1F] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#5A189A]/40";
const labelCls = "mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[#4b5563]";

const STD_ITEMS = [
  { key: "barrel", label: "Barrel", price: 350 },
  { key: "pallet", label: "Pallet", price: 1500 },
  { key: "dbox", label: "D Box", price: 1200 },
];

function Seg({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
        active ? "border-[#5A189A] bg-[#5A189A]/10 text-[#5A189A]" : "border-black/10 bg-white text-[#4b5563] hover:border-[#5A189A]/40"
      }`}>
      {Icon && <Icon size={16} />} {children}
    </button>
  );
}

export default function Calculator() {
  const [s, setS] = useState({
    lane: "usa", usaMode: "air", usaSeaMode: "cbf",
    usaAirWeight: "", seaL: "", seaW: "", seaH: "",
    qtyBarrel: 0, qtyPallet: 0, qtyDbox: 0,
    chinaMode: "sea", chinaCbm: "", chinaAirWeight: "",
  });
  const set = (patch) => setS((p) => ({ ...p, ...patch }));

  const result = useMemo(() => {
    if (s.lane === "usa") {
      if (s.usaMode === "air") {
        const w = parseFloat(s.usaAirWeight) || 0;
        const cost = w <= 0 ? 0 : w <= 1 ? 700 : 700 + (w - 1) * 500;
        return { cost, method: "Air Freight · Miami → Kingston", rows: [["Weight", w ? `${w} lb` : "—"]] };
      }
      if (s.usaSeaMode === "cbf") {
        const l = parseFloat(s.seaL) || 0, w = parseFloat(s.seaW) || 0, h = parseFloat(s.seaH) || 0;
        const cf = (l * w * h) / 1728;
        const raw = cf * 8.5;
        const cost = cf > 0 ? Math.max(raw, 40) : 0;
        return {
          cost, method: "Sea Freight · Cubic Feet",
          rows: [["Cubic feet", cf ? `${cf.toFixed(2)} ft³` : "—"], ["Rate", "$8.50 / ft³"]],
          min: 40, minApplied: cf > 0 && raw < 40,
        };
      }
      const items = STD_ITEMS.map((it) => ({ ...it, qty: s[`qty${it.key[0].toUpperCase()}${it.key.slice(1)}`] || 0 }));
      const cost = items.reduce((a, it) => a + it.price * it.qty, 0);
      const rows = items.filter((it) => it.qty > 0).map((it) => [`${it.label} × ${it.qty}`, money(it.price * it.qty)]);
      return { cost, method: "Sea Freight · Standard Items", rows: rows.length ? rows : [["Items", "—"]] };
    }
    if (s.chinaMode === "air") {
      const w = parseFloat(s.chinaAirWeight) || 0;
      return { cost: w * 10, method: "Air Freight · China → Jamaica", rows: [["Weight", w ? `${w} lb` : "—"], ["Rate", "$10 / lb"]] };
    }
    const cbm = parseFloat(s.chinaCbm) || 0;
    const raw = cbm * 250;
    const cost = cbm > 0 ? Math.max(raw, 250) : 0;
    return {
      cost, method: "Sea Freight · China → Jamaica",
      rows: [["CBM", cbm ? `${cbm} m³` : "—"], ["Rate", "$250 / CBM"]],
      min: 250, minApplied: cbm > 0 && raw < 250,
    };
  }, [s]);

  const goToQuote = () => {
    const summary = `Quote request — ${result.method}. ${result.rows.map(([k, v]) => `${k}: ${v}`).join(", ")}. Estimated cost: ${money(result.cost)} USD.`;
    window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { summary, service: result.method } }));
  };

  const Stepper = ({ item }) => {
    const field = `qty${item.key[0].toUpperCase()}${item.key.slice(1)}`;
    const val = s[field] || 0;
    return (
      <div className="flex items-center justify-between rounded-xl border border-black/10 bg-white px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-[#1F1F1F]">{item.label}</div>
          <div className="text-xs text-[#9ca3af]">{money(item.price)} each</div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid={`qty-${item.key}-minus`} onClick={() => set({ [field]: Math.max(0, val - 1) })}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-black/10 text-[#4b5563] hover:border-[#5A189A]"><Minus size={14} /></button>
          <span className="w-6 text-center font-display text-lg font-bold text-[#1F1F1F]" data-testid={`qty-${item.key}-val`}>{val}</span>
          <button type="button" data-testid={`qty-${item.key}-plus`} onClick={() => set({ [field]: val + 1 })}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-black/10 text-[#4b5563] hover:border-[#5A189A]"><Plus size={14} /></button>
        </div>
      </div>
    );
  };

  return (
    <div className="card-soft rounded-3xl p-6 sm:p-8" data-testid="rate-calculator">
      {/* lane selector */}
      <div className="grid grid-cols-2 gap-3">
        <button type="button" data-testid="lane-usa" onClick={() => set({ lane: "usa" })}
          className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${s.lane === "usa" ? "border-[#5A189A] bg-[#5A189A]/[0.06]" : "border-black/10 hover:border-[#5A189A]/40"}`}>
          <span className="text-2xl leading-none">🇺🇸</span>
          <div>
            <div className="text-sm font-bold text-[#1F1F1F]">USA</div>
            <div className="text-[11px] text-[#9ca3af]">Miami → Kingston</div>
          </div>
        </button>
        <button type="button" data-testid="lane-china" onClick={() => set({ lane: "china" })}
          className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${s.lane === "china" ? "border-[#5A189A] bg-[#5A189A]/[0.06]" : "border-black/10 hover:border-[#5A189A]/40"}`}>
          <span className="text-2xl leading-none">🇨🇳</span>
          <div>
            <div className="text-sm font-bold text-[#1F1F1F]">China</div>
            <div className="text-[11px] text-[#9ca3af]">China → Jamaica</div>
          </div>
        </button>
      </div>

      {/* mode tabs */}
      <div className="mt-5 flex gap-3">
        {s.lane === "usa" ? (
          <>
            <Seg testid="usa-air" active={s.usaMode === "air"} onClick={() => set({ usaMode: "air" })} icon={Plane}>Air Freight</Seg>
            <Seg testid="usa-sea" active={s.usaMode === "sea"} onClick={() => set({ usaMode: "sea" })} icon={Ship}>Sea Freight</Seg>
          </>
        ) : (
          <>
            <Seg testid="china-air" active={s.chinaMode === "air"} onClick={() => set({ chinaMode: "air" })} icon={Plane}>Air Freight</Seg>
            <Seg testid="china-sea" active={s.chinaMode === "sea"} onClick={() => set({ chinaMode: "sea" })} icon={Ship}>Sea Freight</Seg>
          </>
        )}
      </div>

      {/* inputs */}
      <div className="mt-6">
        <AnimatePresence mode="wait">
          <motion.div key={`${s.lane}-${s.usaMode}-${s.chinaMode}-${s.usaSeaMode}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>

            {s.lane === "usa" && s.usaMode === "air" && (
              <div>
                <label className={labelCls}>Package weight (lbs)</label>
                <input data-testid="usa-air-weight" type="number" min="0" step="0.1" placeholder="e.g. 5"
                  value={s.usaAirWeight} onChange={(e) => set({ usaAirWeight: e.target.value })} className={inputCls} />
                <p className="mt-2 text-xs text-[#9ca3af]">First lb $700 · each additional lb $500</p>
              </div>
            )}

            {s.lane === "usa" && s.usaMode === "sea" && (
              <div>
                <div className="mb-4 flex gap-3">
                  <Seg testid="sea-cbf" active={s.usaSeaMode === "cbf"} onClick={() => set({ usaSeaMode: "cbf" })} icon={Ruler}>Cubic Feet</Seg>
                  <Seg testid="sea-items" active={s.usaSeaMode === "items"} onClick={() => set({ usaSeaMode: "items" })} icon={Boxes}>Standard Items</Seg>
                </div>
                {s.usaSeaMode === "cbf" ? (
                  <div>
                    <div className="grid grid-cols-3 gap-3">
                      {[["Length", "seaL"], ["Width", "seaW"], ["Height", "seaH"]].map(([lbl, f]) => (
                        <div key={f}>
                          <label className={labelCls}>{lbl} (in)</label>
                          <input data-testid={`sea-${f}`} type="number" min="0" step="0.1" placeholder="0"
                            value={s[f]} onChange={(e) => set({ [f]: e.target.value })} className={inputCls} />
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-[#9ca3af]">$8.50 / ft³ · minimum charge $40</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {STD_ITEMS.map((it) => <Stepper key={it.key} item={it} />)}
                  </div>
                )}
              </div>
            )}

            {s.lane === "china" && s.chinaMode === "air" && (
              <div>
                <label className={labelCls}>Weight (lbs)</label>
                <input data-testid="china-air-weight" type="number" min="0" step="0.1" placeholder="e.g. 20"
                  value={s.chinaAirWeight} onChange={(e) => set({ chinaAirWeight: e.target.value })} className={inputCls} />
                <p className="mt-2 text-xs text-[#9ca3af]">$10 / lb</p>
              </div>
            )}

            {s.lane === "china" && s.chinaMode === "sea" && (
              <div>
                <label className={labelCls}>Volume (CBM)</label>
                <input data-testid="china-cbm" type="number" min="0" step="0.01" placeholder="e.g. 2.5"
                  value={s.chinaCbm} onChange={(e) => set({ chinaCbm: e.target.value })} className={inputCls} />
                <p className="mt-2 text-xs text-[#9ca3af]">$250 / CBM · minimum $250</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* live result */}
      <div className="mt-6 rounded-2xl border border-[#5A189A]/15 bg-[#5A189A]/[0.05] p-5" data-testid="calc-result">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#5A189A]">{result.method}</div>
            <motion.div key={result.cost} initial={{ opacity: 0.4, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className="font-display text-4xl font-extrabold text-[#1F1F1F]" data-testid="calc-cost">
              {money(result.cost)}<span className="ml-1 text-sm font-medium text-[#9ca3af]">USD</span>
            </motion.div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {result.rows.map(([k, v], i) => (
            <div key={i} className="text-[#4b5563]"><span className="text-[#9ca3af]">{k}:</span> {v}</div>
          ))}
          {result.min && <div className="text-[#4b5563]"><span className="text-[#9ca3af]">Min charge:</span> {money(result.min)}{result.minApplied ? " (applied)" : ""}</div>}
        </div>
      </div>

      <a href="#contact" onClick={goToQuote} data-testid="get-quote-btn"
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E31B23] hover:bg-[#B71319] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_12px_32px_-12px_rgba(227,27,35,0.6)]">
        Get a Quote <ArrowUpRight size={16} />
      </a>

      <p className="mt-4 text-xs leading-relaxed text-[#9ca3af]">
        This is an estimate. Final pricing may vary based on package dimensions, contents and customs requirements.
      </p>
    </div>
  );
}
